/**
 * Cart Drawer Fix
 * - Prevents Shopify 429 "Too Many Requests" by serializing cart mutations
 * - Retries once/twice on 429 with backoff
 * - Guards against duplicate event bindings after drawer re-render
 */

(function () {
  // -----------------------------
  // Cart Request Queue + Retry
  // -----------------------------
  const CartRequestQueue = (() => {
    let queue = Promise.resolve();

    const CART_ENDPOINT_MATCH = /\/cart\/(add|change|update)(\.js)?/i;

    function isCartMutation(url, init) {
      if (!url) return false;
      const method = (init && init.method) ? String(init.method).toUpperCase() : "GET";
      if (method === "GET") return false;
      return CART_ENDPOINT_MATCH.test(url);
    }

    function sleep(ms) {
      return new Promise((r) => setTimeout(r, ms));
    }

    async function fetchWithRetry(fetchFn, attempts = 2) {
      let lastResp = null;

      for (let i = 0; i <= attempts; i++) {
        const resp = await fetchFn();
        lastResp = resp;

        // If rate limited, back off + retry
        if (resp && resp.status === 429 && i < attempts) {
          const backoff = i === 0 ? 350 : 900;
          await sleep(backoff);
          continue;
        }

        return resp;
      }

      return lastResp;
    }

    function enqueue(fetchFn) {
      // Ensure cart mutations happen sequentially
      queue = queue.then(() => fetchFn()).catch(() => fetchFn());
      return queue;
    }

    return {
      isCartMutation,
      enqueue,
      fetchWithRetry,
    };
  })();

  // Patch window.fetch ONLY for cart mutations to prevent 429 floods.
  const originalFetch = window.fetch.bind(window);
  window.fetch = function (resource, init) {
    const url = typeof resource === "string" ? resource : (resource && resource.url) ? resource.url : "";

    // Only serialize POSTs to cart add/change/update
    if (CartRequestQueue.isCartMutation(url, init)) {
      return CartRequestQueue.enqueue(() =>
        CartRequestQueue.fetchWithRetry(() => originalFetch(resource, init), 2)
      );
    }

    return originalFetch(resource, init);
  };

  // -----------------------------
  // Cart Drawer Component
  // -----------------------------
  class CartDrawer extends HTMLElement {
    constructor() {
      super();

      // Guard: prevent duplicate bindings if this element gets reconnected/re-rendered
      if (this.dataset.bound === "true") return;
      this.dataset.bound = "true";

      this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());

      const overlay = this.querySelector("#CartDrawer-Overlay");
      if (overlay) {
        overlay.addEventListener("click", this.close.bind(this));
      }

      this.setHeaderCartIconAccessibility();
      this.bindUpsellButton();
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector("#cart-icon-bubble");
      if (!cartLink) return;

      // Guard: prevent double-binding on re-render
      if (cartLink.dataset.drawerBound === "true") return;
      cartLink.dataset.drawerBound = "true";

      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");

      cartLink.addEventListener("click", (event) => {
        event.preventDefault();
        this.open(cartLink);
      });

      cartLink.addEventListener("keydown", (event) => {
        if (event.code && event.code.toUpperCase() === "SPACE") {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    bindUpsellButton() {
      // Guard: prevent duplicate click delegation bindings
      if (this.dataset.upsellBound === "true") return;
      this.dataset.upsellBound = "true";

      this.addEventListener("click", (event) => {
        const upsellButton = event.target.closest(".upsell-add-button");
        if (!upsellButton) return;

        event.preventDefault();

        const form = upsellButton.closest(".upsell-form");
        if (!form) return;

        const input = form.querySelector('input[name="id"]');
        const variantId = input ? input.value : null;
        if (!variantId) return;

        this.addItemToCart(variantId);
      });
    }

    async addItemToCart(variantId) {
      const sections = this.getSectionsToRender().map((section) => section.id);

      const body = JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: sections,
        sections_url: window.location.pathname,
      });

      try {
        const response = await fetch(`${routes.cart_add_url}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body,
        });

        if (response.status === 422) {
          const errorData = await response.json();
          console.error("Error adding item to cart:", errorData.description);
          return;
        }

        const parsedState = await response.json();
        this.renderContents(parsedState);
      } catch (error) {
        console.error("Error adding item to cart:", error);
      }
    }

    startCountdownTimer() {
      const timerElement = document.getElementById("cart-countdown-timer");
      if (!timerElement) return;

      let minutes = 10;
      let seconds = 0;

      if (this.countdownInterval) clearInterval(this.countdownInterval);

      const updateTimer = () => {
        timerElement.textContent = `${minutes.toString().padStart(2, "0")}:${seconds
          .toString()
          .padStart(2, "0")}`;
      };

      updateTimer();

      this.countdownInterval = setInterval(() => {
        if (seconds === 0) {
          if (minutes === 0) {
            clearInterval(this.countdownInterval);
            return;
          }
          minutes--;
          seconds = 59;
        } else {
          seconds--;
        }

        updateTimer();
      }, 1000);
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);

      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) {
        this.setSummaryAccessibility(cartDrawerNote);
      }

      setTimeout(() => {
        this.classList.add("animate", "active");
      });

      this.addEventListener(
        "transitionend",
        () => {
          const containerToTrapFocusOn = this.classList.contains("is-empty")
            ? this.querySelector(".drawer__inner-empty")
            : document.getElementById("CartDrawer");
          const focusElement =
            this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");

          if (typeof trapFocus === "function") {
            trapFocus(containerToTrapFocusOn, focusElement);
          }
        },
        { once: true }
      );

      document.body.classList.add("overflow-hidden");

      // Slick slider init (guarded)
      const $slider = window.jQuery ? window.jQuery(".gbfrequently-bought-with-main-whole-slider") : null;
      if ($slider && $slider.length > 0 && !$slider.hasClass("slick-initialized")) {
        $slider.slick({
          infinite: true,
          autoplaySpeed: 1000,
          autoplay: false,
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        });
        $slider.css("opacity", "1");
      } else if ($slider && $slider.length > 0) {
        $slider.css("opacity", "1");
      }

      this.startCountdownTimer();
    }

    close() {
      const drawerEl = document.querySelector("cart-drawer");
      const cartCustomizationMode = drawerEl && drawerEl.classList.contains("customization-mode");

      if (cartCustomizationMode) {
        alert(
          "Cart Customization Mode is active. Disable it in Theme Settings > Cart Drawer to allow closing."
        );
        return;
      }

      this.classList.remove("active");

      if (typeof removeTrapFocus === "function") {
        removeTrapFocus(this.activeElement);
      }

      document.body.classList.remove("overflow-hidden");

      if (this.countdownInterval) clearInterval(this.countdownInterval);
    }

    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute("role", "button");
      cartDrawerNote.setAttribute("aria-expanded", "false");

      if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute("id")) {
        cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
      }

      cartDrawerNote.addEventListener("click", (event) => {
        event.currentTarget.setAttribute(
          "aria-expanded",
          !event.currentTarget.closest("details").hasAttribute("open")
        );
      });

      if (cartDrawerNote.parentElement) {
        cartDrawerNote.parentElement.addEventListener("keyup", window.onKeyUpEscape);
      }
    }

    renderContents(parsedState) {
      const inner = this.querySelector(".drawer__inner");
      if (inner && inner.classList.contains("is-empty")) {
        inner.classList.remove("is-empty");
      }

      this.productId = parsedState.id;

      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;

        if (section.id === "cart-drawer" || section.selector === "#CartDrawer") {
          const newCartDrawer = this.getSectionDOM(parsedState.sections[section.id], section.selector);

          // updateCartDrawer is theme-specific; keep existing behavior
          if (typeof updateCartDrawer === "function") {
            updateCartDrawer(sectionElement, newCartDrawer);
          } else {
            // Fallback: replace drawer contents if helper missing
            sectionElement.innerHTML = newCartDrawer ? newCartDrawer.innerHTML : sectionElement.innerHTML;
          }
        } else {
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }
      });

      // Rebind overlay click safely (no duplicates)
      setTimeout(() => {
        const overlay = this.querySelector("#CartDrawer-Overlay");
        if (overlay && overlay.dataset.bound !== "true") {
          overlay.dataset.bound = "true";
          overlay.addEventListener("click", this.close.bind(this));
        }
        this.open();
      });

      // Slick slider init (guarded)
      const $slider = window.jQuery ? window.jQuery(".gbfrequently-bought-with-main-whole-slider") : null;
      if ($slider && $slider.length > 0 && !$slider.hasClass("slick-initialized")) {
        $slider.slick({
          infinite: true,
          autoplaySpeed: 1000,
          autoplay: false,
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        });
        $slider.css("opacity", "1");
      } else if ($slider && $slider.length > 0) {
        $slider.css("opacity", "1");
      }
    }

    getSectionInnerHTML(html, selector = ".shopify-section") {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const el = doc.querySelector(selector);
      return el ? el.innerHTML : "";
    }

    getSectionsToRender() {
      // Keep your slider logic but guard double-init
      setTimeout(function () {
        const $slider = window.jQuery ? window.jQuery(".gbfrequently-bought-with-main-whole-slider") : null;
        if ($slider && $slider.length > 0 && !$slider.hasClass("slick-initialized")) {
          $slider.slick({
            infinite: true,
            autoplaySpeed: 1000,
            autoplay: false,
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: false,
            dots: true,
          });
          $slider.css("opacity", "1");
        } else if ($slider && $slider.length > 0) {
          $slider.css("opacity", "1");
        }
      }, 500);

      return [
        { id: "cart-drawer", selector: "#CartDrawer" },
        { id: "cart-icon-bubble" },
      ];
    }

    getSectionDOM(html, selector = ".shopify-section") {
      const doc = new DOMParser().parseFromString(html, "text/html");
      return doc.querySelector(selector);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define("cart-drawer", CartDrawer);

  // -----------------------------
  // Cart Drawer Items
  // -----------------------------
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      setTimeout(function () {
        const $slider = window.jQuery ? window.jQuery(".gbfrequently-bought-with-main-whole-slider") : null;
        if ($slider && $slider.length > 0 && !$slider.hasClass("slick-initialized")) {
          $slider.slick({
            infinite: true,
            autoplaySpeed: 1000,
            autoplay: false,
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: false,
            dots: true,
          });
          $slider.css("opacity", "1");
        } else if ($slider && $slider.length > 0) {
          $slider.css("opacity", "1");
        }
      }, 500);

      return [
        { id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" },
        { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".shopify-section" },
      ];
    }
  }

  customElements.define("cart-drawer-items", CartDrawerItems);
})();

